import {
  Flex,
  Button,
  AttributesSelector,
  Box,
  Text,
  Input,
  Checkbox,
} from '@workwhile/ui'
import React, { useMemo } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { filtersSchema, FiltersSchema } from './filtersSchema'
import { useUnpaidShiftsContextValue } from '../../context/UnpaidShiftsProvider'
import { toTitleCase } from 'lib/string'
import { ShiftStatusRender } from '../UnpaidShiftsRenderers/ShiftStatusRender'
import { CompletedDaysAgoFilters } from './CompletedDaysAgoFilters'
import { SelectFilter } from 'components/Filter/SelectFilter'
import {
  workerClassifications,
  WorkerClassification,
} from 'constants/workerClassification'

const workerClassificationOptions = workerClassifications.map((c) => ({
  label: c.label,
  value: c.id,
}))

// Status options
const statusOptions = [
  { label: 'Completed', value: 'completed' },
  { label: 'Admin Review', value: 'admin_review' },
  { label: 'Employer Review', value: 'employer_review' },
  { label: 'Employer Approved', value: 'employer_approved' },
  { label: 'Employer Cancelled', value: 'employer_cancelled' },
  { label: 'Approved', value: 'approved' },
]

// UserInputs component
const UserInputs = () => {
  const methods = useFormContext<FiltersSchema>()

  return (
    <Box>
      <Flex flexDirection="column" gap={2}>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            User ID
          </Text>
          <Input
            type="number"
            placeholder="Enter user ID"
            value={methods.watch('user.userId') || ''}
            onChange={(e) => {
              const value = e.target.value
              methods.setValue(
                'user.userId',
                value ? parseInt(value, 10) : null
              )
            }}
          />
        </Box>
      </Flex>
    </Box>
  )
}

// UserFilters component
const UserFilters = () => {
  const methods = useFormContext<FiltersSchema>()

  const userId = methods.watch('user.userId')

  const resetToDefaultValues = () => {
    methods.setValue('user.userId', null)
  }

  const selectedAttributes = useMemo(() => {
    const attributes: string[] = []

    if (userId) {
      attributes.push(`${userId}`)
    }

    return attributes
  }, [userId])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="User"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key="user"
            title="User"
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            <UserInputs />
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}

// StatusInputs component
const StatusInputs = () => {
  const methods = useFormContext<FiltersSchema>()

  const selectedStatuses = methods.watch('status.statuses') || []

  const handleStatusChange = (status: string, checked: boolean) => {
    const currentStatuses = selectedStatuses
    if (checked) {
      methods.setValue('status.statuses', [...currentStatuses, status])
    } else {
      methods.setValue(
        'status.statuses',
        currentStatuses.filter((s) => s !== status)
      )
    }
  }

  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" mb={2}>
        Select Status
      </Text>
      {statusOptions.map((option) => (
        <Checkbox
          key={option.value}
          value={selectedStatuses.includes(option.value)}
          onChange={(checked) => handleStatusChange(option.value, checked)}
        >
          <ShiftStatusRender value={option.value} />
        </Checkbox>
      ))}
    </Box>
  )
}

// StatusFilters component
const StatusFilters = () => {
  const methods = useFormContext<FiltersSchema>()

  const statuses = methods.watch('status.statuses')

  const resetToDefaultValues = () => {
    methods.setValue('status.statuses', [])
  }

  const selectedAttributes = useMemo(() => {
    return statuses || []
  }, [statuses])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="Status"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes.map((status) =>
          toTitleCase(status.toLowerCase().replace('_', ' '))
        )}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key="status"
            title="Shift Status"
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            <StatusInputs />
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}

// WorkerClassificationFilters component
const WorkerClassificationFilters = () => (
  <SelectFilter<FiltersSchema, WorkerClassification>
    fieldPath="workerClassification.values"
    label="Worker Classification"
    filterKey="workerClassification"
    options={workerClassificationOptions}
    inputLabel="Select Worker Classification"
    formatSelectedAttributes={(values) =>
      values.map(
        (value) =>
          workerClassifications.find((c) => c.id === value)?.label ?? value
      )
    }
  />
)

// ShiftIdInputs component
const ShiftIdInputs = () => {
  const methods = useFormContext<FiltersSchema>()

  return (
    <Box>
      <Flex flexDirection="column" gap={2}>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Shift ID
          </Text>
          <Input
            type="number"
            placeholder="Enter shift ID"
            value={methods.watch('shiftId.id') || ''}
            onChange={(e) => {
              const value = e.target.value
              methods.setValue('shiftId.id', value ? parseInt(value, 10) : null)
            }}
          />
        </Box>
      </Flex>
    </Box>
  )
}

// ShiftIdFilters component
const ShiftIdFilters = () => {
  const methods = useFormContext<FiltersSchema>()

  const shiftId = methods.watch('shiftId.id')

  const resetToDefaultValues = () => {
    methods.setValue('shiftId.id', null)
  }

  const selectedAttributes = useMemo(() => {
    const attributes: string[] = []

    if (shiftId) {
      attributes.push(`${shiftId}`)
    }

    return attributes
  }, [shiftId])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="Shift"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key="shiftId"
            title="Shift"
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            <ShiftIdInputs />
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}

// CompanyIdInputs component
const CompanyIdInputs = () => {
  const methods = useFormContext<FiltersSchema>()

  return (
    <Box>
      <Flex flexDirection="column" gap={2}>
        <Box>
          <Text fontSize="sm" fontWeight="medium" mb={1}>
            Company ID
          </Text>
          <Input
            type="number"
            placeholder="Enter company ID"
            value={methods.watch('companyId.id') || ''}
            onChange={(e) => {
              const value = e.target.value
              methods.setValue(
                'companyId.id',
                value ? parseInt(value, 10) : null
              )
            }}
          />
        </Box>
      </Flex>
    </Box>
  )
}

// CompanyIdFilters component
const CompanyIdFilters = () => {
  const methods = useFormContext<FiltersSchema>()

  const companyId = methods.watch('companyId.id')

  const resetToDefaultValues = () => {
    methods.setValue('companyId.id', null)
  }

  const selectedAttributes = useMemo(() => {
    const attributes: string[] = []

    if (companyId) {
      attributes.push(`${companyId}`)
    }

    return attributes
  }, [companyId])

  return (
    <FormProvider {...methods}>
      <AttributesSelector
        label="Company"
        rootGroupTitle="Filter by"
        selectedAttributes={selectedAttributes}
        onClearAll={resetToDefaultValues}
        groups={
          <AttributesSelector.Group
            key="companyId"
            title="Company"
            numberOfSelectedAttributes={selectedAttributes.length}
          >
            <CompanyIdInputs />
          </AttributesSelector.Group>
        }
      />
    </FormProvider>
  )
}

interface UnpaidShiftsFiltersProps {
  hideUserFilter?: boolean
}

export const UnpaidShiftsFilters = ({
  hideUserFilter = false,
}: UnpaidShiftsFiltersProps) => {
  const {
    state: { filters },
    actions: { setFilters },
  } = useUnpaidShiftsContextValue()

  const methods = useForm<FiltersSchema>({
    resolver: zodResolver(filtersSchema),
    reValidateMode: 'onChange',
    mode: 'all',
    defaultValues: {
      user: {
        userId: hideUserFilter ? null : filters.userId,
      },
      shiftId: {
        id: filters.shiftId,
      },
      companyId: {
        id: filters.companyId,
      },
      status: {
        statuses: filters.status || [],
      },
      workerClassification: {
        values: (filters.workerClassification as WorkerClassification[]) || [],
      },
      completedDaysAgo: {
        days: filters.completedDaysAgo,
      },
    },
  })

  const onSubmit = (data: FiltersSchema) => {
    setFilters({
      userId: data.user.userId,
      shiftId: data.shiftId.id,
      companyId: data.companyId.id,
      status: data.status.statuses.length > 0 ? data.status.statuses : null,
      workerClassification:
        data.workerClassification.values.length > 0
          ? data.workerClassification.values
          : null,
      completedDaysAgo: data.completedDaysAgo.days,
    })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Flex gap={2} alignItems="center" flexWrap="wrap">
          {!hideUserFilter && <UserFilters />}
          <ShiftIdFilters />
          <CompanyIdFilters />
          <StatusFilters />
          <WorkerClassificationFilters />
          <CompletedDaysAgoFilters />
          <Button type="submit" variant="primary">
            Search
          </Button>
        </Flex>
      </form>
    </FormProvider>
  )
}
